import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';

import { filterCommonAttributes } from '../ai-answer-dialog/analytics-helpers';
import type {
	commonAttributesType,
	NonGenerativeAIAnswerResultShownProps,
} from '../ai-answer-dialog/types';

import type { KnowledgeCardsIntentType } from './types';

interface KnowledgeCardLinkClickProps extends commonAttributesType {
	location: string;
	position?: number;
	shownAfterCardBodyExpanded?: boolean;
}

export const onKnowledgeCardShown = (
	props: commonAttributesType & { cardType: KnowledgeCardsIntentType },
): AnalyticsEventPayload => {
	const { source, cardType } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerDialog',
		actionSubjectId: 'knowledgeCard',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			cardType,
		},
	};
};

export const onKnowledgeCardLinkClick = (
	props: KnowledgeCardLinkClickProps,
): AnalyticsEventPayload => {
	const { source, location, position, shownAfterCardBodyExpanded } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerDialog',
		actionSubjectId: 'knowledgeCardLink',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			location,
			position,
			shownAfterCardBodyExpanded,
		},
	};
};

// Fires the same event as onKnowledgeCardLinkClick but with less required attributes
// since some experiences don't have a plain text answer (e.g. Jira Issue Knowledge Card)
export const onNonGenerativeKnowledgeCardLinkClick = (
	props: NonGenerativeAIAnswerResultShownProps,
): AnalyticsEventPayload => {
	const {
		answerString,
		brand,
		query,
		queryHash,
		searchSessionId,
		source,
		targetMarker,
		answerCardType,
		location,
	} = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerDialog',
		actionSubjectId: 'knowledgeCardLink',
		source: source,
		eventType: 'track',
		attributes: {
			brand,
			queryHash,
			searchSessionId,
			targetMarker,
			location,
			answerCardType,
		},
		nonPrivacySafeAttributes: {
			answerString,
			query,
		},
	};
};

export const onAgentKnowledgeCardCarouselShown = (
	props: commonAttributesType & { agentCount: number },
): AnalyticsEventPayload => {
	const { source, agentCount } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAgentKnowledgeCardCarousel',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			agentCount,
		},
	};
};

export const onAgentKnowledgeCardCarouselItemClick = (
	props: commonAttributesType & { agentId: string; position: number },
): AnalyticsEventPayload => {
	const { source, agentId, position } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAgentKnowledgeCardCarousel',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			agentId,
			position,
		},
	};
};

export const onIndividualAgentKnowledgeCardShown = (
	props: commonAttributesType & { agentId: string },
): AnalyticsEventPayload => {
	const { source, agentId } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAgentKnowledgeCard',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			agentId,
		},
	};
};

export const onPersonCardOrgChartShown = (props: commonAttributesType): AnalyticsEventPayload => {
	const { source } = props;
	return {
		action: 'shown',
		actionSubject: 'personCardOrgChartModal',
		source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
	};
};

export const onPersonCardOrgChartDwelled = (props: commonAttributesType): AnalyticsEventPayload => {
	const { source } = props;
	return {
		action: 'dwelled',
		actionSubject: 'personCardOrgChartModal',
		source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
	};
};

export const onPersonCardOrgChartCardClicked = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { source } = props;
	return {
		action: 'clicked',
		actionSubject: 'personCardOrgChartCard',
		source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
	};
};
